import type { MaskedPlayerLane as PlayerLane } from '@completto/shared';
import { LANE_SIZE, laneRevealedCount } from '@completto/shared';
import { html, View } from 'rune-ts';
import type { HoldingCardView } from './HoldingCard.ts';
import { LaneCellView, type LaneCellViewState } from './LaneCell.ts';

export type MyLaneInsertMode = 'NONE' | 'INSERT_DRAG';

export type MyLaneData = {
  lane: PlayerLane;
  placeableSlots: ReadonlySet<number>;
  inZoneSlots: ReadonlySet<number>;
  selectedIdx: number | null;
  insertMode: MyLaneInsertMode;
  insertFromIdx: number | null;
  justPlacedIdx: number | null;
  isMyTurn: boolean;
  canStartDrag: boolean;
  /** 헤더 우측에 표시할 손타일 compact 뷰 (가로 모드에서만 노출). 없으면 hidden. */
  holdingBadge?: HoldingCardView;
  onCellClick: (idx: number) => void;
  onCellPointerDown: (
    idx: number,
    pointerId: number,
    clientX: number,
    clientY: number,
    el: HTMLElement,
  ) => void;
};

export class MyLaneView extends View<MyLaneData> {
  override template() {
    const items: ReturnType<typeof html>[] = [];
    const slots = this.data.lane.slots;
    for (let idx = 0; idx < slots.length; idx++) {
      const slot = slots[idx];
      if (!slot) continue;
      if (idx > 0) {
        items.push(html`
          <div class="lane-gap" data-lane-gap-idx="${idx}" aria-hidden="true"></div>
        `);
      }
      const cell = new LaneCellView({
        tile: slot.tile,
        idx,
        state: this.computeState(idx),
        onClick: this.data.onCellClick,
        onPointerDown: this.data.onCellPointerDown,
      });
      items.push(html`${cell}`);
    }

    const revealed = laneRevealedCount(this.data.lane);
    const hint = this.data.canStartDrag
      ? '비공개 타일을 끌어 다른 자리로 옮기기'
      : '좌→우 오름차순으로 22칸을 모두 공개하면 승리';
    const cardCls = this.data.isMyTurn
      ? 'my-lane-card relative rounded-2xl bg-wood-paper ring-2 ring-card-gold/50 shadow-lg shadow-card-gold/10 px-3 pt-2 pb-3 mlandscape:px-1.5 mlandscape:pt-2 mlandscape:pb-3'
      : 'my-lane-card relative rounded-2xl bg-wood-paper/70 ring-1 ring-wood-sand px-3 pt-2 pb-3 mlandscape:px-1.5 mlandscape:pt-2 mlandscape:pb-3';
    const dotCls = this.data.isMyTurn ? 'bg-card-gold' : 'bg-wood-sand';
    const progressCls = this.data.isMyTurn ? 'text-card-brass' : 'text-wood-coffee/70';

    const badge = this.data.holdingBadge;
    return html`
      <section class="${cardCls}">
        <div class="my-lane-header flex items-center gap-2 mb-2 select-none">
          <span class="inline-block w-2 h-2 rounded-full ${dotCls}"></span>
          <span class="text-caption font-bold uppercase tracking-wider text-wood-ink">MY LANE</span>
          <span class="text-caption font-mono tabular-nums ${progressCls}">${revealed}/${LANE_SIZE}</span>
          ${
            badge ? html`<div class="hidden mlandscape:block ml-2 shrink-0">${badge}</div>` : html``
          }
          <span class="text-caption text-wood-coffee/65 ml-auto truncate mlandscape:hidden">${hint}</span>
        </div>
        <div class="my-lane flex items-stretch gap-0 pt-1 pb-1 mlandscape:pt-2 mlandscape:pb-2">
          ${items}
        </div>
      </section>
    `;
  }

  private computeState(idx: number): LaneCellViewState {
    const slot = this.data.lane.slots[idx];
    if (!slot) return 'DOWN';
    const isUp = slot.tile.face === 'UP';

    if (this.data.insertMode === 'INSERT_DRAG') {
      if (this.data.insertFromIdx === idx) return 'INSERT_SOURCE';
      // gap 인디케이터는 GamePage에서 DOM 직접 조작 (re-render 회피)
      return isUp ? 'UP' : 'DOWN';
    }

    if (this.data.justPlacedIdx === idx) return 'JUST_PLACED';
    if (isUp) return 'UP';

    if (this.data.selectedIdx === idx) return 'SELECTED';
    if (this.data.placeableSlots.has(idx)) return 'PLACEABLE';
    if (this.data.inZoneSlots.has(idx)) return 'IN_ZONE';

    // 비공개 + 내 차례 + holding 없음 + IDLE → 드래그 가능
    if (this.data.canStartDrag) return 'DOWN_DRAGGABLE';
    return 'DOWN';
  }
}
